import { bossMarkSvg } from '../render/elementArt';
import { formatTime } from '../core/util';
import { BOSS_BY_ID, BOSS_ROSTER } from '../data/bosses';
import { COMMANDERS, COMMANDER_BY_ID } from '../data/commanders';
import { LOCKED_UNITS } from '../data/collection';
import { ELEMENT_OF, ELEMENT_STYLE } from '../data/elements';
import { FAMILIES, FAMILY_BY_ID, RARITY_LABEL } from '../data/families';
import { STANDARD_RULESET } from '../data/ruleset';
import { SHOP } from '../data/shop';
import type { BoardMode, Difficulty } from '../data/types';
import type { CardReward, Profile } from '../meta/profile';
import { cardCopies, cardLevel, critBonus, critPercent, unitPower, upgradeBlock, upgradeCost } from '../meta/profile';
import { minionMarkSvg, towerMarkSvg } from '../render/towerMark';
import { APP_VERSION, aggregate, type MatchRecord, type Settings } from '../telemetry/telemetry';

export interface Setup {
  commander: string;
  difficulty: Difficulty;
  board: BoardMode;
}

const FACTION_TINT: Record<string, string> = {
  purple: '#b13cff',
  red: '#ff4b3a',
  blue: '#4aa3ff',
  green: '#3dcb6e',
  sand: '#e0b15c',
  gold: '#ffb020',
  skirmish: '#1aa89a',
  hex: '#39d98a',
};

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

type HubTab = 'home' | 'units' | 'store';

const ICO = {
  store:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 8h12l-1.2 12H7.2L6 8z"/><path d="M9 8V7a3 3 0 0 1 6 0v1"/></svg>',
  units:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="11" height="15" rx="2"/><rect x="10" y="3" width="11" height="15" rx="2"/></svg>',
  battle:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4l8 8-8 8V4z"/></svg>',
  stats:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19V10M12 19V5M19 19v-7"/></svg>',
};

function dock(active: HubTab): string {
  const tab = (id: HubTab, icon: string, label: string): string =>
    `<button class="dock-btn ${active === id ? 'on' : ''}" data-action="nav" data-id="${id}"><span class="dock-ico">${icon}</span><span>${label}</span></button>`;
  return `<nav class="dock">
    ${tab('store', ICO.store, 'Store')}
    ${tab('units', ICO.units, 'Units')}
    <button class="dock-btn battle ${active === 'home' ? 'on' : ''}" data-action="nav" data-id="home"><span class="dock-ico">${ICO.battle}</span><span>Battle</span></button>
    <button class="dock-btn" data-action="stats"><span class="dock-ico">${ICO.stats}</span><span>Stats</span></button>
  </nav>`;
}

function metaChips(meta?: { gold: number; crit: number }): string {
  if (!meta) return '';
  return `<div class="hub-chips"><span class="gold-chip"><b>${meta.gold}</b> gold</span><span class="crit-chip">${meta.crit}% crit</span></div>`;
}

function hubTop(title: string, sub: string, meta?: { gold: number; crit: number }): string {
  return `<div class="hub-top">
    <div>
      <div class="hub-kicker">${esc(sub)}</div>
      <h2>${esc(title)}</h2>
    </div>
    ${metaChips(meta)}
    <button class="btn icon ghost" data-action="settings" aria-label="Settings">⚙</button>
  </div>`;
}

const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary'] as const;

/** Turret mark for this family. Rank is the merge step, not the card level. */
function towerPortrait(familyId: string, height: number, rank = 1): string {
  const px = Math.round(height * 0.92);
  return `<span class="tower-art">${towerMarkSvg(familyId, px, rank)}</span>`;
}

function detailPortrait(familyId: string, rank: number): string {
  return `<span class="unit-pair">${towerMarkSvg(familyId, 92, rank)}${minionMarkSvg(familyId, 92, rank)}</span>`;
}

function mergeFactor(perRank: number, rank: number): number {
  return 1 + perRank * (rank - 1);
}

function prettyAttack(attack: string): string {
  if (attack === 'support') return 'Aura';
  return attack.charAt(0).toUpperCase() + attack.slice(1);
}

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Thin helper that renders a screen into the overlay root and wires data-action buttons. */
export class Screens {
  constructor(private readonly root: HTMLElement) {}

  clear(): void {
    this.root.innerHTML = '';
  }

  private show(html: string, cls = ''): HTMLElement {
    this.clear();
    const el = document.createElement('div');
    el.className = `screen ${cls}`.trim();
    el.innerHTML = html;
    this.root.appendChild(el);
    return el;
  }

  private on(el: HTMLElement, handlers: Record<string, (target: HTMLElement) => void>): void {
    let fromPointer = false;
    let startX = 0;
    let startY = 0;
    const fire = (ev: Event): void => {
      const target = (ev.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!target || target.hasAttribute('disabled')) return;
      const action = target.dataset.action;
      if (!action || !handlers[action]) return;
      ev.preventDefault();
      handlers[action](target);
    };
    el.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      startX = ev.clientX;
      startY = ev.clientY;
    });
    el.addEventListener('pointerup', (ev) => {
      if (ev.button !== 0) return;
      if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > 14) return;
      const target = (ev.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!target || target.hasAttribute('disabled')) return;
      const action = target.dataset.action;
      if (!action || !handlers[action]) return;
      fromPointer = true;
      window.setTimeout(() => {
        fromPointer = false;
      }, 400);
      ev.preventDefault();
      handlers[action](target);
    });
    el.addEventListener('click', (ev) => {
      if (fromPointer) {
        fromPointer = false;
        return;
      }
      fire(ev);
    });
  }

  // ---------------------------------------------------------------- loading

  loading(progress: number): void {
    const existing = this.root.querySelector<HTMLElement>('.loading .progress i');
    if (existing) {
      existing.style.width = `${Math.round(progress * 100)}%`;
      return;
    }
    this.show(
      `<div class="logo" style="font-size:40px">PEASENTS</div>
       <div class="progress"><i style="width:${Math.round(progress * 100)}%"></i></div>
       <div class="tagline">Loading</div>`,
      'loading title-screen',
    );
  }

  // ------------------------------------------------------------------ title

  title(
    handlers: {
      play: () => void;
      pve: () => void;
      howto: () => void;
      stats: () => void;
      settings: () => void;
      units: () => void;
      store: () => void;
    },
    tutorialPending: boolean,
    meta: { gold: number; crit: number; online: boolean },
  ): void {
    const owned = FAMILIES.length;
    const total = owned + LOCKED_UNITS.length;
    const el = this.show(
      `       <div class="hub-top">
         <div class="hub-kicker">Demo ${APP_VERSION}</div>
         ${metaChips(meta)}
         <button class="btn icon ghost" data-action="settings" aria-label="Settings">⚙</button>
       </div>
       <div class="scroll hub-home">
         <div class="crest">
           <div class="diorama" aria-hidden="true">
             <span class="fort"></span>
             <span class="lane left"></span>
             <span class="lane right"></span>
             <span class="fort you"></span>
           </div>
           <div class="logo">PEASENTS</div>
           <div class="tagline">Two lanes. One fortress. The bonds decide it.</div>
         </div>
         <div class="home-actions">
           <div class="play-stack">
             <button class="battle-cta pve" data-action="pve">
               <span class="battle-kicker">${tutorialPending ? 'First match' : 'PvE · on this device'}</span>
               <span class="battle-title">${tutorialPending ? 'Start the lesson' : 'Play here'}</span>
               <span class="battle-sub">${tutorialPending ? 'A short walk through your first match' : 'You against a bot. Starts now.'}</span>
             </button>
             <button class="battle-cta quiet" data-action="play">
               <span class="battle-copy">
                 <span class="battle-kicker">PvP · online</span>
                 <span class="battle-title">Find a player</span>
               </span>
               <span class="battle-go">›</span>
             </button>
           </div>
           <div class="home-row">
             <button class="home-tile units" data-action="units">
               <b>${owned}/${total}</b>
               <span>Units in your fortress</span>
             </button>
             <button class="home-tile guide" data-action="howto">
               <b>Guide</b>
               <span>How a match is played</span>
             </button>
           </div>
           <p class="notice">Every match is saved on this device.</p>
         </div>
       </div>
       ${dock('home')}`,
      'hub',
    );
    this.on(el, {
      play: handlers.play,
      pve: handlers.pve,
      howto: handlers.howto,
      stats: handlers.stats,
      settings: handlers.settings,
      nav: (t) => {
        const id = t.dataset.id;
        if (id === 'units') handlers.units();
        else if (id === 'store') handlers.store();
        else if (id === 'home') handlers.play();
      },
      units: handlers.units,
    });
  }

  units(
    profile: Profile,
    handlers: {
      play: () => void;
      stats: () => void;
      settings: () => void;
      home: () => void;
      store: () => void;
      upgrade: (familyId: string) => void;
      saveCompany: (deck: string[], bosses: string[]) => void;
    },
  ): void {
    let roster: 'units' | 'bosses' | 'locked' = 'units';
    let selected: { kind: 'owned' | 'locked'; id: string } | null = null;
    let deck = [...profile.deck];
    let bosses = [...profile.bosses];
    let armUnit = 0;
    let armBoss = 0;
    let mergeRank = 1;
    const owned = FAMILIES.length;
    const total = owned + LOCKED_UNITS.length;
    const render = (): string => {
      const unitSlots = deck
        .map((id, i) => {
          const f = FAMILY_BY_ID[id];
          const on = roster !== 'bosses' && i === armUnit;
          return `<button class="pick ${on ? 'on' : ''}" data-action="arm" data-kind="unit" data-index="${i}" style="--unit:${ELEMENT_STYLE[ELEMENT_OF[id]].color};--glow:${ELEMENT_STYLE[ELEMENT_OF[id]].hot}">
              <span class="pick-art">${f ? towerPortrait(f.id, 48) : ''}</span>
              <span class="pick-name">${esc(f?.name ?? 'Empty')}</span>
            </button>`;
        })
        .join('');
      const bossSlots = bosses
        .map((id, i) => {
          const b = BOSS_ROSTER.find((x) => x.id === id);
          const tint = FACTION_TINT[b?.faction ?? ''] ?? '#fff';
          const on = roster === 'bosses' && i === armBoss;
          const name = (b?.name ?? 'Empty').replace(/^The /, '');
          return `<button class="pick boss ${on ? 'on' : ''}" data-action="arm" data-kind="boss" data-index="${i}" style="--unit:${tint};--glow:${tint}">
              <span class="pick-art">${b ? bossMarkSvg(b.id,64) : ''}</span>
              <span class="pick-name">${esc(name)}</span>
            </button>`;
        })
        .join('');
      const replacingUnit = FAMILY_BY_ID[deck[armUnit]]?.name ?? 'a unit';
      const replacingBoss = (BOSS_ROSTER.find((b) => b.id === bosses[armBoss])?.name ?? 'a boss').replace(/^The /, '');
      const status =
        roster === 'bosses'
          ? `Tap a boss to replace ${replacingBoss}.`
          : roster === 'locked'
            ? 'The roster is recruited. New families will appear here.'
            : `Tap a unit to replace ${replacingUnit}.`;
      const cards =
        roster === 'locked'
          ? LOCKED_UNITS.length
            ? LOCKED_UNITS.map(
              (u) => `<article class="unit-card locked">
                <button class="card-main" data-action="open" data-kind="locked" data-id="${u.id}" aria-label="Locked unit"></button>
                <span class="unit-badge">Locked</span>
                <span class="unit-art mystery"><span>?</span></span>
                <span class="unit-role">${esc(u.role)}</span>
                <span class="unit-name">Undiscovered</span>
                <span class="unit-family">Not recruited</span>
              </article>`,
            ).join('')
            : `<p class="notice">Every family is in the fortress. Rarity is on the unit card.</p>`
          : roster === 'bosses'
            ? BOSS_ROSTER.map((b) => {
                const tint = FACTION_TINT[b.faction] ?? '#fff';
                const at = bosses.indexOf(b.id);
                const name = b.name.replace(/^The /, '');
                return `<article class="boss-card ${at >= 0 ? 'in-deck' : ''} ${at === armBoss ? 'is-target' : ''}" style="--unit:${tint}">
                  <button class="card-main" data-action="pickboss" data-id="${b.id}" aria-label="${esc(name)}"></button>
                  ${bossMarkSvg(b.id,80)}
                  <span>${esc(name)}</span>
                  ${b.aura ? `<small>${esc(b.aura.label)}</small>` : ''}
                  ${at >= 0 ? `<i class="slot-flag">${at + 1}</i>` : ''}
                </article>`;
              }).join('')
            : RARITY_ORDER.map((tier) => {
                const group = FAMILIES.filter((f) => f.rarity === tier).sort((a, b) => a.name.localeCompare(b.name));
                const cardsInTier = group
                  .map((f) => {
                    const at = deck.indexOf(f.id);
                    return `<article class="unit-card ${at >= 0 ? 'in-deck' : ''} ${at === armUnit ? 'is-target' : ''}" style="--unit:${ELEMENT_STYLE[ELEMENT_OF[f.id]].color};--glow:${ELEMENT_STYLE[ELEMENT_OF[f.id]].hot}">
                  <button class="card-main" data-action="pickunit" data-id="${f.id}" aria-label="${esc(f.name)}"></button>
                  <button class="card-info" data-action="open" data-kind="owned" data-id="${f.id}" aria-label="Details">i</button>
                  <span class="unit-badge">Lv ${cardLevel(profile, f.id)}</span>
                  ${at >= 0 ? `<i class="slot-flag">${at + 1}</i>` : ''}
                  <span class="unit-art">${towerPortrait(f.id, 78, 1)}</span>
                  <span class="unit-role" style="color:${ELEMENT_STYLE[ELEMENT_OF[f.id]].color}">${ELEMENT_STYLE[ELEMENT_OF[f.id]].name} · ${esc(f.role)}</span>
                  <span class="rarity ${f.rarity}">${RARITY_LABEL[f.rarity]}</span>
                  <span class="unit-name">${esc(f.minion.name)}</span>
                  <span class="unit-family">${esc(f.name)} · ${cardCopies(profile, f.id)} cards</span>
                </article>`;
                  })
                  .join('');
                return `<h3 class="rarity-break ${tier}">${RARITY_LABEL[tier]}</h3>${cardsInTier}`;
              }).join('');
      const tabs = (['units', 'bosses', 'locked'] as const)
        .map((id) => `<button data-action="filter" data-id="${id}" class="${roster === id ? 'active' : ''}">${id}</button>`)
        .join('');
      return `${hubTop('Units', `${owned} of ${total} recruited`, { gold: profile.gold, crit: critPercent(profile) })}
        <div class="scroll units-page">
          <section class="company-board">
            <div class="company-head">
              <div>
                <div class="hub-kicker">Your deck</div>
                <strong>Comes to every match</strong>
              </div>
              <p>${esc(status)}</p>
            </div>
            <div class="company-label">Units <em>5</em></div>
            <div class="company-row">${unitSlots}</div>
            <div class="company-label">Bosses <em>3</em></div>
            <div class="company-row bosses">${bossSlots}</div>
          </section>
          <div class="seg unit-filter">${tabs}</div>
          <div class="${roster === 'bosses' ? 'boss-grid' : 'unit-grid'}">${cards}</div>
        </div>
        ${dock('units')}
        ${selected ? this.inspect(selected, profile, selected.kind === 'owned' && deck.includes(selected.id), FAMILY_BY_ID[deck[armUnit]]?.name ?? 'the selected slot', mergeRank) : ''}`;
    };
    const el = this.show(render(), 'hub');
    const rerender = (): void => {
      el.innerHTML = render();
    };
    this.on(el, {
      play: handlers.play,
      stats: handlers.stats,
      settings: handlers.settings,
      filter: (t) => {
        const next = t.dataset.id;
        if (next === 'units' || next === 'bosses' || next === 'locked') roster = next;
        rerender();
      },
      arm: (t) => {
        const index = Number(t.dataset.index);
        if (t.dataset.kind === 'boss') {
          armBoss = index;
          roster = 'bosses';
        } else {
          armUnit = index;
          roster = 'units';
        }
        rerender();
      },
      pickunit: (t) => {
        const id = t.dataset.id!;
        const at = deck.indexOf(id);
        if (at >= 0) armUnit = at;
        else {
          deck[armUnit] = id;
          armUnit = (armUnit + 1) % deck.length;
          handlers.saveCompany(deck, bosses);
        }
        rerender();
      },
      pickboss: (t) => {
        const id = t.dataset.id!;
        const at = bosses.indexOf(id);
        if (at >= 0) armBoss = at;
        else {
          bosses[armBoss] = id;
          armBoss = (armBoss + 1) % bosses.length;
          handlers.saveCompany(deck, bosses);
        }
        rerender();
      },
      open: (t) => {
        const id = t.dataset.id!;
        if (!selected || selected.id !== id) mergeRank = 1;
        selected = { kind: t.dataset.kind === 'locked' ? 'locked' : 'owned', id };
        rerender();
      },
      merge: (t) => {
        const next = Number(t.dataset.rank);
        if (next >= 1 && next <= 5) mergeRank = next;
        rerender();
      },
      enlist: () => {
        if (selected?.kind !== 'owned') return;
        const id = selected.id;
        const at = deck.indexOf(id);
        if (at >= 0) {
          armUnit = at;
        } else {
          deck[armUnit] = id;
          armUnit = (armUnit + 1) % deck.length;
          handlers.saveCompany(deck, bosses);
        }
        selected = null;
        rerender();
      },
      close: () => {
        selected = null;
        rerender();
      },
      noop: () => {},
      upgrade: () => {
        if (selected?.kind === 'owned') handlers.upgrade(selected.id);
        rerender();
      },
      nav: (t) => {
        const id = t.dataset.id;
        if (id === 'store') handlers.store();
        else if (id === 'home') handlers.home();
      },
    });
  }

  private inspect(selected: { kind: 'owned' | 'locked'; id: string }, profile: Profile, inCompany: boolean, slotName: string, mergeRank = 1): string {
    if (selected.kind === 'locked') {
      const u = LOCKED_UNITS.find((x) => x.id === selected.id);
      if (!u) return '';
      return `<div class="inspect" data-action="close">
        <div class="inspect-sheet locked" data-action="noop">
          <div class="mystery big">?</div>
          <div class="unit-role">${esc(u.role)}</div>
          <h3>Undiscovered</h3>
          <p>${esc(u.hint)}</p>
          <p class="hint">This family is not in the demo. It will show up here once it joins the fortress.</p>
          <button class="btn" data-action="close">Close</button>
        </div>
      </div>`;
    }
    const f = FAMILY_BY_ID[selected.id];
    if (!f) return '';
    const tower = f.tower;
    const m = f.minion;
    const level = cardLevel(profile, f.id);
    const copies = cardCopies(profile, f.id);
    const cost = upgradeCost(level);
    const block = upgradeBlock(profile, f.id);
    const power = unitPower(level) * (1 + critBonus(profile.levels));
    const nextLevels = { ...profile.levels, [f.id]: level + 1 };
    const nextPower = cost ? unitPower(level + 1) * (1 + critBonus(nextLevels)) : power;
    const scale = STANDARD_RULESET.rankScaling;
    const rank = Math.max(1, Math.min(5, mergeRank));
    const baseHp = Math.round(m.hp * power);
    const baseDmg = m.damage * power;
    const hp = Math.round(m.hp * power * mergeFactor(scale.minionHp, rank));
    const dmg = m.damage * power * mergeFactor(scale.minionDamage, rank);
    const mass = m.mass * (1 + 0.12 * (rank - 1));
    const muster = f.spawnInterval / mergeFactor(scale.spawnRate, rank);
    const towerDmg = tower.damage * power * mergeFactor(scale.towerDamage, rank);
    const fire = tower.fireInterval / mergeFactor(scale.towerFireRate, rank);
    const towerLine =
      tower.attack === 'support'
        ? `Buffs adjacent towers +${Math.round((tower.support ?? 0) * rank * power * 100)}% damage.`
        : `${prettyAttack(tower.attack)} · ${num(towerDmg)} dmg · every ${fire.toFixed(2)}s · range ${tower.range}`;
    const upgrade = cost
      ? `<button class="btn primary" data-action="upgrade" ${block ? 'disabled' : ''}>Upgrade to level ${level + 1}</button>
         <p class="hint">${esc(block ?? `${cost.gold} gold · ${cost.copies} cards · health ${baseHp} → ${Math.round(m.hp * nextPower)}, damage ${num(baseDmg)} → ${num(m.damage * nextPower)}`)}</p>`
      : `<p class="hint">Max level. This unit is as strong as the demo allows.</p>`;
    return `<div class="inspect" data-action="close">
      <div class="inspect-sheet" data-action="noop" style="--unit:${ELEMENT_STYLE[ELEMENT_OF[f.id]].color};--glow:${ELEMENT_STYLE[ELEMENT_OF[f.id]].hot}">
        <div class="inspect-art">${detailPortrait(f.id, rank)}</div>
        <div class="merge-toggle">
          <button type="button" data-action="merge" data-rank="${rank - 1}" ${rank <= 1 ? 'disabled' : ''} aria-label="Previous merge">‹</button>
          <strong>Merge ${rank}</strong>
          <button type="button" data-action="merge" data-rank="${rank + 1}" ${rank >= 5 ? 'disabled' : ''} aria-label="Next merge">›</button>
        </div>
        <div class="rank-ladder" aria-label="Merge ranks">
          ${[1, 2, 3, 4, 5].map((step) => `<button type="button" data-action="merge" data-rank="${step}" class="${step === rank ? 'on' : ''}" aria-label="Merge ${step}"><i>${step}</i>${towerMarkSvg(f.id, 36, step)}</button>`).join('')}
        </div>
        <div class="unit-role" style="color:${ELEMENT_STYLE[ELEMENT_OF[f.id]].color}">${ELEMENT_STYLE[ELEMENT_OF[f.id]].name} · ${esc(f.role)}</div>
        <span class="rarity ${f.rarity}">${RARITY_LABEL[f.rarity]}</span>
        <h3>${esc(f.minion.name)}</h3>
        <div class="inspect-family">Level ${level} · ${copies} cards · ${critPercent(profile)}% crit</div>
        <p>${esc(ELEMENT_STYLE[ELEMENT_OF[f.id]].blurb)}</p>
        <p>${esc(f.description)}</p>
        <div class="inspect-block"><b>Tower</b><span>${esc(towerLine)}</span></div>
        <div class="inspect-block"><b>Minion</b><span>${esc(m.description)}</span></div>
        <div class="stat-row">
          <div><b>${hp}</b><span>Health</span></div>
          <div><b>${num(dmg)}</b><span>Damage</span></div>
          <div><b>${m.speed}</b><span>Speed</span></div>
          <div><b>${num(mass)}</b><span>Mass</span></div>
          <div><b>${muster.toFixed(1)}s</b><span>Muster</span></div>
        </div>
        ${upgrade}
        <button class="btn teal" data-action="enlist">${inCompany ? 'Already in your deck' : `Replace ${esc(slotName)}`}</button>
        <button class="btn ghost" data-action="close">Close</button>
      </div>
    </div>`;
  }

  store(handlers: { play: () => void; stats: () => void; settings: () => void; home: () => void; units: () => void }, meta?: { gold: number; crit: number }): void {
    const el = this.show(
      `${hubTop('Store', 'The merchant', meta)}
       <div class="scroll store-home">
         <div class="merchant">
           <div class="chest" aria-hidden="true"></div>
           <div>
             <div class="hub-kicker">Closed for the demo</div>
             <h3>Nothing is for sale yet</h3>
             <p>Chests, gold, and recruits will open here later. Matches you play are already saved on this device.</p>
           </div>
         </div>
       </div>
       ${dock('store')}`,
      'hub',
    );
    this.on(el, {
      play: handlers.play,
      stats: handlers.stats,
      settings: handlers.settings,
      nav: (t) => {
        const id = t.dataset.id;
        if (id === 'units') handlers.units();
        else if (id === 'home') handlers.home();
      },
    });
  }

  // ------------------------------------------------------------------ setup

  setup(
    initial: Setup,
    seedLabel: string,
    handlers: { start: (s: Setup, mode: 'online' | 'pve') => void; back: () => void },
    onlineNote = '',
    playUrl = '',
    initialMode: 'online' | 'pve' = 'online',
  ): void {
    const state: Setup = { ...initial };
    let mode = initialMode;
    const render = (): string => `
      <div class="header"><h2>${mode === 'pve' ? 'Play here' : 'Find a player'}</h2><button class="btn icon ghost" data-action="back" aria-label="Back">✕</button></div>
      <div class="scroll">
        <p class="lede">How should a tower choose its lane?</p>
        <div class="choice-list">
          <button class="choice ${state.board === 'arrows' ? 'on' : ''}" data-action="board" data-id="arrows">
            <b>Tap to switch</b>
            <span>The tower stays on its cell. Tap it to send minions down the left or the right lane.</span>
          </button>
          <button class="choice ${state.board === 'painted' ? 'on' : ''}" data-action="board" data-id="painted">
            <b>Colored cells</b>
            <span>The cell is the lane. Dragging a tower to another cell costs 5 Essence. Merges stay free.</span>
          </button>
        </div>
        <p class="notice">${esc(mode === 'pve' ? 'The match runs on this device.' : onlineNote || 'The other player brings their own deck.')}</p>
        ${mode === 'online' && playUrl ? `<div class="field"><label>Open this on the other phone or iPad</label><input type="text" readonly value="${esc(playUrl)}" /><div class="hint">${/192\.168\.|10\.|localhost|127\.0\.0\.1/.test(playUrl) ? 'Same Wi-Fi as this computer. If Safari says the connection is not private, tap Show Details, then visit this website.' : 'Send this link, then both press Find a player.'}</div></div>` : ''}
        <p class="seed-line">Match seed <span class="mono">${esc(seedLabel)}</span></p>
      </div>
      <div class="footer"><button class="btn ${mode === 'pve' ? 'teal' : 'primary'}" data-action="start">${mode === 'pve' ? 'Start match' : 'Find a player'}</button></div>`;
    const el = this.show(render());
    const rerender = (): void => {
      el.innerHTML = render();
    };
    this.on(el, {
      back: handlers.back,
      start: () => handlers.start(state, mode),
      board: (t) => {
        const next = t.dataset.id;
        if (next === 'arrows' || next === 'painted') state.board = next;
        rerender();
      },
    });
  }

  // ------------------------------------------------------------------ howto

  howto(onBack: () => void): void {
    const steps = [
      ['Summon', 'Spend Essence to place a random tower from your deck on a random empty tile of your 5x3 fortress. Every summon costs more than the last. Merging does not lower the price.'],
      ['Route your minions', 'Regular: tap a tower to flip it between LEFT and RIGHT. A tower cannot change cells. Colored cells: each cell is a lane color, the tower sends minions that way, and dragging it to another cell costs 5 Essence.'],
      ['Merge', 'Drag a tower onto another of the same family and rank. It becomes a random other unit from your five, one rank higher, and keeps the destination tile and its lane. A merge can break an element bond, because the unit changes element.'],
      ['Elements', 'Every unit is Fire, Water, Earth, or Wind. Neighbouring towers, not diagonals, each form their own bond and share it with their minions. A tower can hold four bonds. A minion can hold six. One bond doubles when that pair’s minions also walk together. Three in a straight line with the same element on both ends, such as Earth Fire Earth, strengthens those bonds. A straight line that contains all four elements strengthens them again and grants a small extra gift. Two of the same element resonate a little. Water with Earth, and Fire with Wind, are the strongest. Water with Fire, and Earth with Wind, hurt each other.'],
      ['Shop', 'Four board-wide branches: Barrage (tower damage), Tempo (fire & muster speed), Muster (minion health & damage), Greed (Essence economy).'],
      ['Defend & break through', 'Towers shoot enemies within range - front rows and outer columns cover their lane best. Minions that reach the enemy wall take a heart. Six hearts each; rank 4+ elites and bosses take two.'],
      ['Bosses & Sudden Death', 'Every 90 seconds every minion on the field vanishes. Each player sends one of their three bosses down a random lane, and the minions that just vanished make that boss tougher. The two bosses may clash in the same lane, or march past each other on different lanes. Fresh minions hold for 5 seconds after the bosses appear. You start with 400 Essence. Lane posts pay a steady wage while you hold them, and a higher merge rank is worth more on each kill. At 4:30 Sudden Death begins: gates fall, leaks deal double and siege engines march until a fortress breaks. At 6:30 the fortress with more hearts wins.'],
    ];
    const el = this.show(
      `<div class="header"><h2>How to play</h2><button class="btn icon ghost" data-action="back">✕</button></div>
       <div class="scroll howto">
         ${steps.map(([h, p], i) => `<div class="card"><div class="num">${i + 1}</div><div><h3>${h}</h3><p>${p}</p></div></div>`).join('')}
         <div class="section-title">Tower families</div>
         <div class="card">
           ${FAMILIES.map((f) => `<div class="family-row"><span class="swatch" style="background:${ELEMENT_STYLE[ELEMENT_OF[f.id]].color}"></span><div class="txt"><b>${f.name}</b> · ${ELEMENT_STYLE[ELEMENT_OF[f.id]].name} · ${f.role} — ${esc(f.description)}</div></div>`).join('')}
         </div>
         <div class="section-title">Shop branches</div>
         <div class="card">
           ${SHOP.map((s) => `<div class="family-row"><span class="swatch" style="background:${s.color}"></span><div class="txt"><b>${s.name}</b> — ${esc(s.description)}</div></div>`).join('')}
         </div>
         <div class="section-title">Commanders</div>
         <div class="card">
           ${COMMANDERS.map((c) => `<div class="family-row"><span class="swatch" style="background:${c.color}"></span><div class="txt"><b>${c.name}</b> — ${esc(c.passiveDescription)} <b>${esc(c.activeName)}</b>: ${esc(c.activeDescription)}</div></div>`).join('')}
         </div>
         <div style="height:12px"></div>
       </div>
       <div class="footer"><button class="btn primary" data-action="back">Got it</button></div>`,
    );
    this.on(el, { back: onBack });
  }

  // ------------------------------------------------------------------ pause

  pause(settings: Settings, handlers: { resume: () => void; restart: () => void; howto: () => void; quit: () => void; toggleSound: () => void; toggleMusic: () => void }, note = ''): void {
    const el = this.show(
      `<div class="sheet-wrap">
         <div class="sheet">
           <div class="hub-kicker">Match</div>
           <h2>Paused</h2>
           ${note ? `<p class="notice">${esc(note)}</p>` : ''}
           <button class="btn primary" data-action="resume">Resume</button>
           <button class="btn" data-action="howto">How to play</button>
           <div class="sheet-row">
             <button class="btn ghost" data-action="sound">${settings.sound ? 'Sound on' : 'Sound off'}</button>
             <button class="btn ghost" data-action="music">${settings.music ? 'Music on' : 'Music off'}</button>
           </div>
           <button class="btn" data-action="restart">Restart match</button>
           <button class="btn danger" data-action="quit">Quit to menu</button>
         </div>
       </div>`,
      'translucent',
    );
    this.on(el, {
      resume: handlers.resume,
      restart: handlers.restart,
      howto: handlers.howto,
      quit: handlers.quit,
      sound: handlers.toggleSound,
      music: handlers.toggleMusic,
    });
  }

  // ---------------------------------------------------------------- results

  searching(state: { secondsLeft: number; detail: string; found: boolean }, onCancel: () => void): void {
    const time = this.root.querySelector<HTMLElement>('[data-role="search-time"]');
    const detail = this.root.querySelector<HTMLElement>('[data-role="search-detail"]');
    if (time && detail && this.root.querySelector('.search-screen')) {
      time.textContent = state.found ? '✓' : String(state.secondsLeft);
      detail.textContent = state.detail;
      const btn = this.root.querySelector<HTMLElement>('[data-action="cancel"]');
      if (btn) btn.style.display = state.found ? 'none' : '';
      return;
    }
    const el = this.show(
      `<div class="search-panel">
         <div class="hub-kicker">Matchmaking</div>
         <div class="search-time" data-role="search-time">${state.found ? '✓' : state.secondsLeft}</div>
         <p data-role="search-detail">${esc(state.detail)}</p>
         <button class="btn ghost" data-action="cancel" style="${state.found ? 'display:none' : ''}">Cancel</button>
       </div>`,
      'search-screen',
    );
    this.on(el, { cancel: onCancel });
  }

  results(record: MatchRecord, handlers: { again: () => void; menu: () => void; feedback: (f: NonNullable<MatchRecord['feedback']>) => void; export: () => void }, reward?: CardReward | null): void {
    const s = record.summary;
    const you = s.player;
    const them = s.opponent;
    const cmd = COMMANDER_BY_ID[you.commander];
    const title = s.result === 'win' ? 'Victory' : s.result === 'loss' ? 'Defeat' : 'Draw';
    const fb = { stars: 0, again: null as boolean | null, text: '' };
    const topFamily = Object.entries(you.familiesSummoned).sort((a, b) => b[1] - a[1])[0];
    const best = you.finalBoard.reduce<(typeof you.finalBoard)[number] | null>((a, t) => (!a || t.rank > a.rank ? t : a), null);
    const bestName = best ? FAMILY_BY_ID[best.family]?.name ?? best.family : '';
    const leftNet = you.leaksDealt[0] - you.leaksTaken[0];
    const rightNet = you.leaksDealt[1] - you.leaksTaken[1];
    const laneLine =
      leftNet === rightNet
        ? 'Both lanes finished even.'
        : leftNet > rightNet
          ? 'The left lane carried the leaks.'
          : 'The right lane carried the leaks.';
    const bossLine = (you.bosses ?? [])
      .map((id) => {
        const b = BOSS_BY_ID[id];
        return b?.aura ? `${b.name.replace(/^The /, '')} (${b.aura.label})` : b?.name.replace(/^The /, '') ?? id;
      })
      .join(', ');
    const deckLine = (you.deck ?? []).map((id) => FAMILY_BY_ID[id]?.name ?? id).join(', ');
    const row = (label: string, a: string | number, b: string | number): string =>
      `<div class="h">${label}</div><div class="v you">${a}</div><div class="v them">${b}</div>`;
    const el = this.show(
      `<div class="scroll">
        <div class="result-hero ${s.result}">
          <h1>${title}</h1>
          <div class="reason">${esc(s.reason)} · ${formatTime(s.durationSec)}${s.overtime ? ' · sudden death' : ''}</div>
          <div class="hearts"><span class="heart-pill you"><b>${you.hearts}</b> You</span><span class="heart-pill them"><b>${them.hearts}</b> Enemy</span></div>
          ${reward ? `<div class="reward">+${reward.gold} gold · +${reward.copies} ${esc(reward.familyName)}</div>` : ''}
        </div>

        <div class="section-title">What the deck did</div>
        <div class="card">
          <p>${laneLine} Your best tower finished at rank <b>${you.highestRank}</b>${bestName ? ` (${esc(bestName)})` : ''}.</p>
          ${deckLine ? `<p>Deck: <b>${esc(deckLine)}</b>.</p>` : ''}
          ${bossLine ? `<p>Bosses buffed the whole match: <b>${esc(bossLine)}</b>.</p>` : ''}
          ${topFamily ? `<p>Most summoned: <b>${esc(FAMILY_BY_ID[topFamily[0]]?.name ?? topFamily[0])}</b> ×${topFamily[1]}.</p>` : ''}
        </div>

        <div class="section-title">Lane report</div>
        <div class="card">
          <div class="lane-bars">
            <div class="lane-bar left"><div class="name">◄ LEFT LANE</div><div class="nums"><span>Dealt <b>${you.leaksDealt[0]}</b></span><span>Taken <b>${you.leaksTaken[0]}</b></span></div></div>
            <div class="lane-bar right"><div class="name">RIGHT LANE ►</div><div class="nums"><span>Dealt <b>${you.leaksDealt[1]}</b></span><span>Taken <b>${you.leaksTaken[1]}</b></span></div></div>
          </div>
          <p style="margin-top:10px">You switched lanes <b>${you.laneSwitches}</b> times (${s.laneSwitchesPerMinute}/min)${
            you.avgTimeToFirstSwitch !== null ? `, first switch after <b>${you.avgTimeToFirstSwitch}s</b> on average` : ''
          }. Minions mustered: <b style="color:var(--lane-left)">${you.minionsMustered[0]} left</b> / <b style="color:var(--lane-right)">${you.minionsMustered[1]} right</b>.</p>
        </div>

        <div class="section-title">Match stats</div>
        <div class="card">
          <div class="stat-grid">
            <div class="h"></div><div class="v you">YOU</div><div class="v them">ENEMY</div>
            ${row('Summons', you.summons, them.summons)}
            ${row('Merges', you.merges, them.merges)}
            ${row('Highest rank', you.highestRank, them.highestRank)}
            ${row('Lane switches', you.laneSwitches, them.laneSwitches)}
            ${row('Shop levels', Object.values(you.shop).reduce((a, b) => a + b, 0), Object.values(them.shop).reduce((a, b) => a + b, 0))}
            ${row('Kills', you.kills, them.kills)}
            ${row('Boss kills', you.bossKills, them.bossKills)}
            ${row('Damage dealt', you.damageDealt.toLocaleString(), them.damageDealt.toLocaleString())}
            ${row('Essence earned', you.essenceEarned.toLocaleString(), them.essenceEarned.toLocaleString())}
            ${row('Commander uses', you.commanderUses, them.commanderUses)}
          </div>
          <p style="margin-top:10px">Commander <b style="color:${cmd.color}">${esc(cmd.name)}</b> vs <b>${esc(COMMANDER_BY_ID[them.commander].name)}</b> (${record.difficulty})${
            topFamily ? ` · most summoned: <b style="color:${FAMILY_BY_ID[topFamily[0]]?.color ?? '#fff'}">${FAMILY_BY_ID[topFamily[0]]?.name ?? topFamily[0]}</b> x${topFamily[1]}` : ''
          }${record.mode === 'online' ? ' · live opponent' : ''}</p>
        </div>

        <div class="section-title">30-second feedback</div>
        <div class="card" data-role="feedback">
          <p style="text-align:center">How was this match?</p>
          <div class="stars">${[1, 2, 3, 4, 5].map((n) => `<button data-action="star" data-n="${n}">★</button>`).join('')}</div>
          <p style="text-align:center">Would you play another?</p>
          <div class="chips"><button class="chip" data-action="again-yes">Yes</button><button class="chip" data-action="again-no">No</button></div>
          <div class="field"><textarea id="fb-text" placeholder="What felt great? What was confusing?"></textarea></div>
          <button class="btn teal" data-action="send" style="width:100%">Send feedback</button>
        </div>
        <p class="notice">Seed <span class="mono">${record.seed}</span> · match id <span class="mono">${record.id.slice(0, 8)}</span></p>
        <div style="height:8px"></div>
      </div>
      <div class="footer">
        <button class="btn ghost small" data-action="export" style="flex:0 0 auto">Export</button>
        <button class="btn ghost" data-action="menu">Menu</button>
        <button class="btn primary" data-action="again">Play again</button>
      </div>`,
    );
    const refresh = (): void => {
      el.querySelectorAll<HTMLElement>('.stars button').forEach((b) => b.classList.toggle('on', Number(b.dataset.n) <= fb.stars));
      el.querySelector('[data-action="again-yes"]')?.classList.toggle('on', fb.again === true);
      el.querySelector('[data-action="again-no"]')?.classList.toggle('on', fb.again === false);
    };
    this.on(el, {
      again: handlers.again,
      menu: handlers.menu,
      export: handlers.export,
      star: (t) => {
        fb.stars = Number(t.dataset.n);
        refresh();
      },
      'again-yes': () => {
        fb.again = true;
        refresh();
      },
      'again-no': () => {
        fb.again = false;
        refresh();
      },
      send: () => {
        const text = (el.querySelector('#fb-text') as HTMLTextAreaElement).value.trim();
        handlers.feedback({ stars: fb.stars, text, wouldPlayAgain: fb.again });
        const card = el.querySelector('[data-role="feedback"]')!;
        card.innerHTML = '<div class="thanks">Thank you! Your feedback was saved with this match.</div>';
      },
    });
  }

  // ------------------------------------------------------------------ stats

  stats(records: MatchRecord[], handlers: { back: () => void; export: () => void; clear: () => void }): void {
    const a = aggregate(records);
    const winRate = a.matches ? Math.round((a.wins / a.matches) * 100) : 0;
    const famTotal = Object.values(a.familyUsage).reduce((x, y) => x + y, 0) || 1;
    const recent = [...records].reverse().slice(0, 12);
    const el = this.show(
      `<div class="header"><h2>My stats</h2><button class="btn icon ghost" data-action="back">✕</button></div>
       <div class="scroll">
         ${
           a.matches === 0
             ? '<p class="notice" style="padding-top:40px">No matches yet. Play a battle and your lane statistics will appear here.</p>'
             : `
         <div class="kpis">
           <div class="kpi"><b>${a.matches}</b><span>Matches</span></div>
           <div class="kpi"><b>${winRate}%</b><span>Win rate</span></div>
           <div class="kpi"><b>${formatTime(a.avgDuration)}</b><span>Avg length</span></div>
           <div class="kpi"><b>${a.avgLaneSwitches}</b><span>Switches / match</span></div>
           <div class="kpi"><b>${a.avgTimeToFirstSwitch ?? '—'}${a.avgTimeToFirstSwitch !== null ? 's' : ''}</b><span>Time to 1st switch</span></div>
           <div class="kpi"><b>${a.avgMerges}</b><span>Merges / match</span></div>
         </div>
         <div class="section-title">Leaks by lane (all matches)</div>
         <div class="card"><div class="lane-bars">
           <div class="lane-bar left"><div class="name">◄ LEFT</div><div class="nums"><span>Dealt <b>${a.leaksDealt[0]}</b></span><span>Taken <b>${a.leaksTaken[0]}</b></span></div></div>
           <div class="lane-bar right"><div class="name">RIGHT ►</div><div class="nums"><span>Dealt <b>${a.leaksDealt[1]}</b></span><span>Taken <b>${a.leaksTaken[1]}</b></span></div></div>
         </div></div>
         <div class="section-title">Win rate by commander</div>
         <div class="card"><table class="table"><tr><th>Commander</th><th style="text-align:right">Matches</th><th style="text-align:right">Wins</th></tr>
           ${Object.entries(a.byCommander)
             .map(([id, v]) => `<tr><td style="color:${COMMANDER_BY_ID[id]?.color ?? '#fff'}">${COMMANDER_BY_ID[id]?.name ?? id}</td><td class="num">${v.matches}</td><td class="num">${Math.round((v.wins / v.matches) * 100)}%</td></tr>`)
             .join('')}
         </table></div>
         <div class="section-title">Win rate by difficulty</div>
         <div class="card"><table class="table"><tr><th>Difficulty</th><th style="text-align:right">Matches</th><th style="text-align:right">Wins</th></tr>
           ${Object.entries(a.byDifficulty)
             .map(([id, v]) => `<tr><td style="text-transform:capitalize">${id}</td><td class="num">${v.matches}</td><td class="num">${Math.round((v.wins / v.matches) * 100)}%</td></tr>`)
             .join('')}
         </table></div>
         <div class="section-title">Families summoned</div>
         <div class="card">
           ${Object.entries(a.familyUsage)
             .sort((x, y) => y[1] - x[1])
             .map(([id, n]) => {
               const f = FAMILY_BY_ID[id];
               return `<div style="margin-bottom:8px"><div style="display:flex;justify-content:space-between;font-size:12px"><b style="color:${f?.color ?? '#fff'}">${f?.name ?? id}</b><span>${n}</span></div><div class="bar"><i style="width:${Math.round((n / famTotal) * 100)}%;background:${f?.color ?? '#fff'}"></i></div></div>`;
             })
             .join('')}
         </div>
         ${
           a.avgStars !== null || a.wouldPlayAgainPct !== null
             ? `<div class="section-title">Feedback</div><div class="kpis"><div class="kpi"><b>${a.avgStars ?? '—'}</b><span>Avg stars</span></div><div class="kpi"><b>${a.wouldPlayAgainPct ?? '—'}${a.wouldPlayAgainPct !== null ? '%' : ''}</b><span>Would play again</span></div><div class="kpi"><b>${records.filter((r) => r.feedback?.text).length}</b><span>Comments</span></div></div>`
             : ''
         }
         <div class="section-title">Recent matches</div>
         <div class="card"><table class="table"><tr><th>Result</th><th>Commander</th><th style="text-align:right">Time</th><th style="text-align:right">Switches</th><th style="text-align:right">Hearts</th></tr>
           ${recent
             .map(
               (r) =>
                 `<tr><td style="color:${r.summary.result === 'win' ? 'var(--teal)' : r.summary.result === 'loss' ? 'var(--red)' : '#fff'}">${r.summary.result.toUpperCase()}</td><td>${COMMANDER_BY_ID[r.summary.player.commander]?.name.split(' ')[1] ?? ''}</td><td class="num">${formatTime(r.summary.durationSec)}</td><td class="num">${r.summary.player.laneSwitches}</td><td class="num">${r.summary.player.hearts}-${r.summary.opponent.hearts}</td></tr>`,
             )
             .join('')}
         </table></div>`
         }
         <div style="height:12px"></div>
       </div>
       <div class="footer">
         <button class="btn ghost small" data-action="clear" style="flex:0 0 auto">Clear</button>
         <button class="btn" data-action="export">Export JSON</button>
         <button class="btn primary" data-action="back">Back</button>
       </div>`,
    );
    this.on(el, {
      back: handlers.back,
      export: handlers.export,
      clear: () => {
        if (confirm('Delete all locally stored match data?')) handlers.clear();
      },
    });
  }

  // --------------------------------------------------------------- settings

  settings(s: Settings, handlers: { back: () => void; change: (s: Settings) => void; resetTutorial: () => void }): void {
    const state = { ...s };
    const el = this.show(
      `<div class="header"><h2>Settings</h2><button class="btn icon ghost" data-action="back">✕</button></div>
       <div class="scroll">
         <div class="card">
           <div class="toggle-row"><div><div class="label">Sound effects</div></div><button class="switch ${state.sound ? 'on' : ''}" data-action="sound"></button></div>
           <div class="toggle-row"><div><div class="label">Music</div><div class="sub">A soundtrack that follows the fight</div></div><button class="switch ${state.music ? 'on' : ''}" data-action="music"></button></div>
           <div class="toggle-row"><div><div class="label">Tutorial</div><div class="sub">${state.tutorialDone ? 'Completed' : 'Will show on your next match'}</div></div><button class="btn small ghost" data-action="tutorial">Show again</button></div>
         </div>
         <div class="section-title">On this device</div>
         <div class="card">
           <div class="field"><label>Nickname</label><input type="text" id="nickname" maxlength="24" value="${esc(state.nickname)}" placeholder="Optional, shown in exported data" /></div>
           <div class="field"><label>Fixed seed</label><input type="text" id="seed" value="${esc(state.seed)}" placeholder="Leave empty for a random match" /><div class="hint">The same seed and the same inputs replay the same match.</div></div>
         </div>
         <div class="section-title">Sharing results</div>
         <div class="card">
           <div class="field"><label>Telemetry endpoint</label><input type="url" id="telemetry" value="${esc(state.telemetryUrl)}" placeholder="https://your-collector.example/ingest" /><div class="hint">Each finished match is sent here. Leave it empty and the data stays on this device.</div></div>
         </div>
         <div class="section-title">Online</div>
         <div class="card">
           <div class="field"><label>Photon App Id</label><input type="text" id="photon" value="${esc(state.photonAppId)}" placeholder="saved in the game" /><div class="hint">Already saved. Friends never enter it. Replace it only if you create a new Photon app.</div></div>
           <div class="field"><label>Supabase URL</label><input type="url" id="supabase-url" value="${esc(state.supabaseUrl)}" placeholder="https://xxxx.supabase.co" /></div>
           <div class="field"><label>Supabase anon key</label><input type="text" id="supabase-key" value="${esc(state.supabaseAnonKey)}" placeholder="optional" /><div class="hint">Optional. Matchmaking works without this. Use it only if you want matches stored in a database.</div></div>
         </div>
         <div style="height:12px"></div>
       </div>
       <div class="footer"><button class="btn primary" data-action="save">Save</button></div>`,
    );
    const readFields = (): void => {
      state.nickname = (el.querySelector('#nickname') as HTMLInputElement).value.trim();
      state.seed = (el.querySelector('#seed') as HTMLInputElement).value.trim();
      state.telemetryUrl = (el.querySelector('#telemetry') as HTMLInputElement).value.trim();
      state.supabaseUrl = (el.querySelector('#supabase-url') as HTMLInputElement).value.trim();
      state.supabaseAnonKey = (el.querySelector('#supabase-key') as HTMLInputElement).value.trim();
      state.photonAppId = (el.querySelector('#photon') as HTMLInputElement).value.trim();
    };
    this.on(el, {
      back: () => {
        readFields();
        handlers.change(state);
        handlers.back();
      },
      save: () => {
        readFields();
        handlers.change(state);
        handlers.back();
      },
      sound: (t) => {
        state.sound = !state.sound;
        t.classList.toggle('on', state.sound);
        handlers.change(state);
      },
      music: (t) => {
        state.music = !state.music;
        t.classList.toggle('on', state.music);
        handlers.change(state);
      },
      tutorial: () => {
        state.tutorialDone = false;
        handlers.change(state);
        handlers.resetTutorial();
        const sub = el.querySelector('.toggle-row:nth-child(3) .sub');
        if (sub) sub.textContent = 'Will show on your next match';
      },
    });
  }
}
